import React, { createContext, useContext, useEffect, useState } from "react";
import config from "../../../../config/config";
import { EzoicConfig, EzoicContextType } from "../../types";
import { initEzoicBannerControl } from "./bannerControl";

const EzoicContext = createContext<EzoicContextType | undefined>(undefined);

// Global flag to prevent multiple script loads across component remounts
let ezoicScriptLoadingStarted = false;
let ezoicScriptLoadPromise: Promise<void> | null = null;

export const EzoicProvider: React.FC<{ children: React.ReactNode; allowAds?: boolean }> = ({
  children,
  allowAds = false,
}) => {
  const [isEzoicLoaded, setIsEzoicLoaded] = useState(false);
  const ezoicConfig: EzoicConfig = config.ads.ezoic;

  // Load Ezoic scripts function
  const loadEzoicScript = React.useCallback(async (): Promise<void> => {
    // If already loading or loaded, return the existing promise
    if (ezoicScriptLoadPromise) {
      return ezoicScriptLoadPromise;
    }

    // If already started loading, create a check promise
    if (ezoicScriptLoadingStarted) {
      return new Promise((resolve) => {
        const checkInterval = setInterval(() => {
          if (window.ezstandalone && window.ezstandalone.cmd) {
            clearInterval(checkInterval);
            setIsEzoicLoaded(true);
            resolve();
          }
        }, 100);
        
        setTimeout(() => {
          clearInterval(checkInterval);
          setIsEzoicLoaded(true);
          resolve();
        }, 10000);
      });
    }

    // Create and store the loading promise
    ezoicScriptLoadPromise = new Promise((resolve, reject) => {
      // Check if Ezoic is already loaded
      if (window.ezstandalone && window.ezstandalone.cmd) {
        console.log("✅ Ezoic already loaded");
        setIsEzoicLoaded(true);
        ezoicScriptLoadingStarted = true;
        resolve();
        return;
      }

      // Check if scripts already exist
      const existingScript = document.querySelector(
        `script[src*="ezojs.com"]`
      );
      if (existingScript) {
        // Wait for ezstandalone to be available
        const checkInterval = setInterval(() => {
          if (window.ezstandalone && window.ezstandalone.cmd) {
            clearInterval(checkInterval);
            setIsEzoicLoaded(true);
            resolve();
          }
        }, 100);
        
        // Timeout after 10 seconds
        setTimeout(() => {
          clearInterval(checkInterval);
          if (window.ezstandalone && window.ezstandalone.cmd) {
            setIsEzoicLoaded(true);
            resolve();
          } else {
            reject(new Error("Ezoic script timeout"));
          }
        }, 10000);
        return;
      }

      // Mark as started to prevent duplicate loads
      ezoicScriptLoadingStarted = true;
      console.log("🚀 Loading Ezoic scripts (first time)");

      try {
        // Step 1: Check and load privacy/consent scripts only once
        const existingPrivacyScript1 = document.querySelector(
          `script[src*="gatekeeperconsent.com/min.js"]`
        );
        const existingPrivacyScript2 = document.querySelector(
          `script[src*="gatekeeperconsent.com/cmp.min.js"]`
        );

        if (!existingPrivacyScript1) {
          const privacyScript1 = document.createElement("script");
          privacyScript1.src = "https://cmp.gatekeeperconsent.com/min.js";
          privacyScript1.setAttribute("data-cfasync", "false");
          privacyScript1.setAttribute("data-ezoic-privacy-loaded", "true");
          document.head.appendChild(privacyScript1);
          console.log("✅ Privacy script 1 added");
        } else {
          console.log("ℹ️ Privacy script 1 already exists - skipping");
        }

        if (!existingPrivacyScript2) {
          const privacyScript2 = document.createElement("script");
          privacyScript2.src = "https://the.gatekeeperconsent.com/cmp.min.js";
          privacyScript2.setAttribute("data-cfasync", "false");
          privacyScript2.setAttribute("data-ezoic-privacy-loaded", "true");
          document.head.appendChild(privacyScript2);
          console.log("✅ Privacy script 2 added");
        } else {
          console.log("ℹ️ Privacy script 2 already exists - skipping");
        }

        // Step 2: Load main Ezoic header script
        const headerScript = document.createElement("script");
        headerScript.async = true;
        headerScript.src = "//www.ezojs.com/ezoic/sa.min.js";

        headerScript.onload = () => {
          console.log("✅ Ezoic header script loaded successfully");

          // Step 3: Initialize ezstandalone
          window.ezstandalone = window.ezstandalone || {};
          window.ezstandalone.cmd = window.ezstandalone.cmd || [];

          // Step 4: Define and enable placements (REQUIRED by Ezoic)
          window.ezstandalone.cmd.push(function() {
            console.log("🎯 Defining Ezoic placements:", ezoicConfig.placementIds);
            
            // Define all placement IDs
            window.ezstandalone.define(
              ezoicConfig.placementIds.banner,
              ezoicConfig.placementIds.sidebar,
              ezoicConfig.placementIds.inContent,
              ezoicConfig.placementIds.footer
            );
            
            // Enable ads
            window.ezstandalone.enable();
            
            // Display ads
            window.ezstandalone.display();
            
            console.log("✅ Ezoic placements defined, enabled, and displayed");
          });

          console.log("✅ Ezoic initialized");
          setIsEzoicLoaded(true);
          console.info('AD: Ezoic script loaded and initialized');
          ezoicScriptLoadPromise = null; // Clear promise after successful load
          resolve();
        };

        headerScript.onerror = (error) => {
          console.error("❌ Failed to load Ezoic script:", error);
          ezoicScriptLoadPromise = null; // Clear promise on error
          ezoicScriptLoadingStarted = false; // Allow retry
          reject(new Error("Failed to load Ezoic script"));
        };

        document.head.appendChild(headerScript);
        console.log("📤 Ezoic script added to document head");
      } catch (error) {
        console.error("❌ Error loading Ezoic scripts:", error);
        ezoicScriptLoadPromise = null; // Clear promise on error
        ezoicScriptLoadingStarted = false; // Allow retry
        reject(error);
      }
    });

    return ezoicScriptLoadPromise;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load Ezoic scripts when component mounts if enabled
  useEffect(() => {
    // Only attempt to load scripts when ads are explicitly allowed
    if (allowAds && ezoicConfig.enabled && !isEzoicLoaded) {
      // Initialize banner control (singleton - only runs once)
      initEzoicBannerControl();
      
      loadEzoicScript().catch((error) => {
        console.error("Failed to load Ezoic script:", error);
      });
    }
    // Cleanup when disabling ads mid-session: try to call Ezoic API to stop ads and remove scripts
    const cleanupEzoic = () => {
      try {
        // If Ezoic provides a disable API, call it
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (window.ezstandalone && typeof window.ezstandalone.disable === 'function') {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          window.ezstandalone.disable();
        }

        // Force cleanup of banner observer
        try {
          // eslint-disable-next-line import/no-extraneous-dependencies
          const { forceCleanupEzoicBannerControl } = require('./bannerControl');
          if (typeof forceCleanupEzoicBannerControl === 'function') {
            forceCleanupEzoicBannerControl();
          }
        } catch (e) {
          // ignore
        }

        // Remove scripts inserted by Ezoic (header, privacy scripts)
        const selectors = [
          'script[src*="ezojs.com"]',
          'script[src*="gatekeeperconsent.com"]',
          'script[src*="the.gatekeeperconsent.com"]',
        ];
        selectors.forEach((sel) => {
          const scripts = Array.from(document.querySelectorAll(sel));
          scripts.forEach((s) => s.parentNode?.removeChild(s));
        });

        // Remove global variable
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (window.ezstandalone) {
          try {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            delete window.ezstandalone;
          } catch (e) {
            // ignore
          }
        }

        setIsEzoicLoaded(false);
        console.info('AD: Ezoic disabled and cleanup executed (ads removed)');
        // Reset module-level loader flags so scripts can be reloaded on enable
        ezoicScriptLoadingStarted = false;
        ezoicScriptLoadPromise = null;
      } catch (e) {
        // swallow cleanup errors
      }
    };

    if (!allowAds && isEzoicLoaded) {
      cleanupEzoic();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowAds, ezoicConfig.enabled, isEzoicLoaded, loadEzoicScript]);

  const value: EzoicContextType = {
    config: ezoicConfig,
    isEzoicLoaded,
    loadEzoicScript,
  };

  return (
    <EzoicContext.Provider value={value}>{children}</EzoicContext.Provider>
  );
};

export const useEzoic = (): EzoicContextType => {
  const context = useContext(EzoicContext);
  if (!context) {
    throw new Error("useEzoic must be used within an EzoicProvider");
  }
  return context;
};
